import React, { useRef, useEffect } from "react"

import domCleanupManager from "../../../util/customview/DomCleanUpManager"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { GlobalState } from "../../../store"

import { IInitializeCheckListView, initialize } from "../slice"
import { ICheckListViewActions } from ".."

import TopicList from "./cards/TopicList"
import QuestionList from "./cards/QuestionList"

interface Props extends IInitializeCheckListView {
    contextMenuActions: ICheckListViewActions
}

export const CheckListView = ({
    contextMenuActions,
    ...restOfProps
}: Props): JSX.Element => {
    const dispatch = useAppDispatch()

    // ensure the RTF css bleeding is handled
    const containerRef = useRef<HTMLDivElement | null>(null)
    const cleanupHandleRef = useRef<DomCleanupHandle | null>(null)
    useEffect(() => {
        if (!containerRef.current) {
            return
        }

        const handle = domCleanupManager.register({
            element: containerRef.current,
        })

        cleanupHandleRef.current = handle

        // we initialize here, because with the DOM cleanup
        // there is a delay when the classes are cleaned up
        // causing the data to be loaded,
        // while the autogrow textarea is not adjusted to the full content yet
        dispatch(initialize(restOfProps))

        return (): void => {
            handle.unregister()
            cleanupHandleRef.current = null
        }
    }, [])

    const fieldsData = useAppSelector((state: GlobalState) => state.fields)

    useEffect(() => {
        if (fieldsData.isInitialized) {
            cleanupHandleRef.current?.cleanup()
        }
    }, [
        fieldsData.isInitialized,
        fieldsData.isLoading,
        fieldsData.currentFieldInEditMode,
    ])

    const {
        data: { questions, topics },
        labels: { NoData },
    } = useAppSelector((state: GlobalState) => state.audits)

    const hasTopics = !!topics && topics.length > 0
    const hasQuestions = !!questions && questions.length > 0
    const hasContent = hasTopics || hasQuestions

    return (
        <div
            className="tw-flex tw-flex-col tw-gap-2"
            data-ft-component="auditsChecklistView"
            ref={containerRef}
        >
            {hasContent ? (
                <>
                    <TopicList
                        containerElement="topicsContainer"
                        contextMenuActions={contextMenuActions}
                        topics={topics}
                        hasIndentation={false}
                    />

                    <QuestionList
                        containerElement="questionsContainer"
                        contextMenuActions={contextMenuActions}
                        questions={questions}
                        hasIndentation={false}
                    />
                </>
            ) : (
                <div
                    data-ft-component="noResults"
                    className="theme-thumb theme-note tw-m-0 is-dimmed"
                    data-ft-id="noResults"
                >
                    {NoData}
                </div>
            )}
        </div>
    )
}

export default CheckListView
